"use client";

import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Navbar } from "@/components/layout/Navbar";
import { usePageTitle } from "@/lib/usePageTitle";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SentryIssue {
  id: string;
  title: string;
  culprit: string;
  count: string;
  userCount: number;
  firstSeen: string;
  lastSeen: string;
  status: string;
  level: string;
  permalink: string;
  metadata?: { value?: string; type?: string; filename?: string };
  entries?: Array<{ type: string; data: { values?: Array<{ stacktrace?: { frames?: Array<{ filename: string; function: string; lineNo: number }> }; type?: string; value?: string }> } }>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtNum(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function relTime(ts: string) {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function buildAiPrompt(issue: SentryIssue): string {
  const exception = issue.entries?.find((e) => e.type === "exception");
  const frames = exception?.data?.values?.[0]?.stacktrace?.frames ?? [];
  const topFrames = frames.slice(-5).reverse();

  const stackText = topFrames
    .map((f) => `  at ${f.function} (${f.filename}:${f.lineNo})`)
    .join("\n");

  return `## Bug Fix Task: ${issue.title}

**Project:** blockynetworks (BlockyNetworks)
**Issue ID:** ${issue.id}
**Occurrences:** ${fmtNum(Number(issue.count))} events, ${issue.userCount} users affected
**Level:** ${issue.level}
**First Seen:** ${new Date(issue.firstSeen).toLocaleString()}
**Last Seen:** ${new Date(issue.lastSeen).toLocaleString()}
**Culprit:** ${issue.culprit}
**Sentry Link:** ${issue.permalink}

### Error
\`${issue.metadata?.type ?? "Error"}: ${issue.metadata?.value ?? issue.title}\`

### Stack Trace (recent frames)
\`\`\`
${stackText || "(no frames available)"}
\`\`\`

### Task
1. Investigate the root cause of this error
2. Locate the relevant code at \`${issue.culprit}\`
3. Implement a fix
4. Test the fix thoroughly
5. Deploy the fix`;
}

// ─── Sentry Issues Panel ──────────────────────────────────────────────────────

function SentryPanel() {
  const getSentryIssues = useAction(api.admin.getSentryIssues);
  const [result, setResult] = useState<{ issues: SentryIssue[]; error: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSentryIssues({});
      setResult(res as { issues: SentryIssue[]; error: string | null });
    } catch (err) {
      setResult({ issues: [], error: String(err) });
    } finally {
      setLoading(false);
    }
  }, [getSentryIssues]);

  const copy = useCallback(async (issue: SentryIssue) => {
    await navigator.clipboard.writeText(buildAiPrompt(issue));
    setCopiedId(issue.id);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  if (!result) {
    return (
      <div className="rounded-lg border border-border bg-background p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold">Sentry Errors</h2>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
          >
            {loading ? "Loading…" : "Load Issues"}
          </button>
        </div>
        <p className="text-sm text-muted">Click &quot;Load Issues&quot; to fetch recent unresolved errors from Sentry.</p>
      </div>
    );
  }

  if (result.error) {
    return (
      <div className="rounded-lg border border-border bg-background p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold">Sentry Errors</h2>
          <button type="button" onClick={load} className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground">
            Retry
          </button>
        </div>
        <p className="text-sm text-red-400">{result.error}</p>
        {result.error.includes("SENTRY_AUTH_TOKEN") && (
          <p className="mt-2 text-xs text-muted">
            Add <code className="rounded bg-muted/30 px-1">SENTRY_AUTH_TOKEN</code> to your Convex env:{" "}
            <code className="rounded bg-muted/30 px-1">npx convex env set SENTRY_AUTH_TOKEN &lt;token&gt; --prod</code>
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-background p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold">Sentry Errors ({result.issues.length})</h2>
        <button type="button" onClick={load} className="rounded-md border border-border px-3 py-1.5 text-xs font-medium">
          Refresh
        </button>
      </div>

      {result.issues.length === 0 ? (
        <p className="text-sm text-muted">No unresolved issues.</p>
      ) : (
        <div className="space-y-3">
          {result.issues.map((issue) => (
            <div key={issue.id} className="rounded-md border border-border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                        issue.level === "error" || issue.level === "fatal"
                          ? "bg-red-500/15 text-red-400"
                          : issue.level === "warning"
                          ? "bg-yellow-500/15 text-yellow-400"
                          : "bg-muted/20 text-muted"
                      }`}
                    >
                      {issue.level}
                    </span>
                    <a
                      href={issue.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-sm font-medium hover:text-primary hover:underline"
                    >
                      {issue.title}
                    </a>
                  </div>
                  <div className="mt-1 text-xs text-muted">
                    {issue.culprit} · {fmtNum(Number(issue.count))} events · {issue.userCount} users · last {relTime(issue.lastSeen)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => copy(issue)}
                  className="shrink-0 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-foreground/5 transition-colors"
                >
                  {copiedId === issue.id ? "Copied!" : "Copy as AI task"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Activity Entry ───────────────────────────────────────────────────────────

function activityLabel(type: string, userName: string | null | undefined, orgName: string | null | undefined) {
  switch (type) {
    case "user_signup": return `${userName ?? "A user"} signed up`;
    case "org_created": return `${userName ?? "Someone"} created ${orgName ?? "an org"}`;
    case "member_joined": return `${userName ?? "Someone"} joined ${orgName ?? "an org"}`;
    case "message_sent": return `${userName ?? "Someone"} sent a message in ${orgName ?? "an org"}`;
    default: return `${type} by ${userName ?? "unknown"}`;
  }
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminOverviewPage() {
  usePageTitle("Admin: Overview");

  const stats = useQuery(api.stats.getLiveStats);
  const activity = useQuery(api.stats.getRecentActivity);

  const isLoading = stats === undefined;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />
      <main className="flex-1 px-4 py-8 sm:px-6">
        <div className="mx-auto max-w-6xl space-y-6">

          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
              <p className="text-sm text-muted">BlockyNetworks platform overview</p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/admin/subscriptions"
                className="rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-foreground/5"
              >
                Subscriptions
              </Link>
              <Link
                to="/admin/billing"
                className="rounded-md border border-border px-3 py-1.5 text-sm font-medium hover:bg-foreground/5"
              >
                Billing
              </Link>
              <Link
                to="/dashboard"
                className="text-sm text-primary hover:underline"
              >
                Dashboard
              </Link>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              { label: "Total Users", value: stats?.totalUsers, suffix: "" },
              { label: "Active Orgs", value: stats?.totalOrganizations, suffix: "" },
              { label: "Online Now", value: stats?.onlineNow, suffix: " players" },
              { label: "New Today", value: stats?.recentSignups, suffix: " signups" },
            ].map((card) => (
              <div key={card.label} className="rounded-lg border border-border bg-background p-4">
                <div className="text-xs text-muted">{card.label}</div>
                <div className="mt-1 text-2xl font-bold">
                  {isLoading ? (
                    <span className="inline-block h-7 w-12 animate-pulse rounded bg-muted/30" />
                  ) : (
                    <>
                      {fmtNum(card.value ?? 0)}
                      {card.suffix && <span className="text-sm font-normal text-muted">{card.suffix}</span>}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Weekly Growth */}
          {stats && (
            <div className="rounded-lg border border-border bg-background p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div>
                  <div className="text-xs text-muted">This week signups</div>
                  <div className="text-lg font-bold">{stats.currentWeekSignups}</div>
                </div>
                <div>
                  <div className="text-xs text-muted">Last week signups</div>
                  <div className="text-lg font-bold">{stats.previousWeekSignups}</div>
                </div>
                <div>
                  <div className="text-xs text-muted">Weekly growth</div>
                  <div className={`text-lg font-bold ${stats.weeklyGrowthDelta >= 0 ? "text-green-400" : "text-red-400"}`}>
                    {stats.weeklyGrowthDelta >= 0 ? "+" : ""}{stats.weeklyGrowthDelta}
                    {stats.useWeeklyGrowthRate && stats.weeklyGrowthRate !== null && (
                      <span className="ml-1 text-sm font-normal">({stats.weeklyGrowthRate}%)</span>
                    )}
                  </div>
                </div>
                <div className="ml-auto text-xs text-muted">
                  Last updated {new Date(stats.timestamp).toLocaleTimeString()}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Trending Servers */}
            <div className="rounded-lg border border-border bg-background p-4">
              <h2 className="mb-3 text-base font-semibold">Trending Servers</h2>
              {isLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="h-8 animate-pulse rounded bg-muted/20" />
                  ))}
                </div>
              ) : stats?.trending.length === 0 ? (
                <p className="text-sm text-muted">No public servers yet.</p>
              ) : (
                <div className="space-y-2">
                  {stats?.trending.map((org, idx) => (
                    <div key={org.id} className="flex items-center gap-3 rounded-md border border-border/50 p-2.5">
                      <span className="w-5 shrink-0 text-center text-xs font-bold text-muted">
                        {idx + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{org.name}</div>
                        <div className="text-xs text-muted">{org.slug}</div>
                      </div>
                      <div className="shrink-0 text-right text-xs text-muted">
                        <div>{fmtNum(org.memberCount)} members</div>
                        <div>{org.onlineCount} online</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="rounded-lg border border-border bg-background p-4">
              <h2 className="mb-3 text-base font-semibold">Recent Activity</h2>
              {activity === undefined ? (
                <div className="space-y-2">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="h-6 animate-pulse rounded bg-muted/20" />
                  ))}
                </div>
              ) : activity.length === 0 ? (
                <p className="text-sm text-muted">No recent activity.</p>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {activity.map((entry) => (
                    <div key={entry.id} className="flex items-center gap-2 text-sm">
                      <span
                        className={`shrink-0 rounded px-1.5 py-0.5 text-xs ${
                          entry.type === "user_signup"
                            ? "bg-green-500/15 text-green-400"
                            : entry.type === "org_created"
                            ? "bg-blue-500/15 text-blue-400"
                            : "bg-muted/20 text-muted"
                        }`}
                      >
                        {entry.type.replace("_", " ")}
                      </span>
                      <span className="truncate text-foreground/80">
                        {activityLabel(entry.type, entry.userName, entry.orgName)}
                      </span>
                      <span className="ml-auto shrink-0 text-xs text-muted">
                        {relTime(new Date(entry.createdAt).toISOString())}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sentry Errors */}
          <SentryPanel />

        </div>
      </main>
    </div>
  );
}
